"use client";

import { Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Link } from "@/components/common/Link";
import {
  AddToCartButton,
  EcommerceProductsProvider,
  ProductSortDropdown,
  ProductsFilter,
  useEcommerceProducts,
} from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";

declare const wvcClient: any;

function ShopGridConsumer() {
  const {
    products,
    loading,
    error,
    refetch,
    hasNext,
    hasPrev,
    currentPage,
    totalPages,
    nextPage,
    prevPage,
    setPage,
  } = useEcommerceProducts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
        {[...Array(6)].map((_, i) => (
          <div key={i} data-index={i} className="space-y-6">
            <div className="aspect-[4/5] w-full animate-pulse bg-muted/40" />
            <div className="h-4 w-2/3 animate-pulse bg-muted/40" />
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-16 text-center">
        <p className="font-default text-sm text-muted-foreground">
          Unable to load our travel packages.
        </p>
        <Button
          onClick={refetch}
          variant="link"
          className="mt-3 px-0 font-default text-sm font-medium text-foreground decoration-accent-foreground underline underline-offset-4"
        >
          Try again
        </Button>
      </div>
    );
  }

  if (products.length === 0) {
    return (
      <div className="py-24 text-center">
        <p className="font-serif text-2xl font-light tracking-tight text-foreground">
          Our travel itineraries are <em className="italic">being curated</em>.
        </p>
        <p className="mt-3 font-default text-sm text-muted-foreground">
          New destinations will be available shortly.
        </p>
      </div>
    );
  }

  const pageNumbers: number[] = [];
  for (let i = 1; i <= totalPages; i++) pageNumbers.push(i);

  return (
    <div className="space-y-20">
      <div className="grid grid-cols-1 gap-x-10 gap-y-16 sm:grid-cols-2 lg:grid-cols-3">
        {products.map((product, i) => (
          <article key={product.id} data-index={i} className="group flex flex-col">
            <Link to={product.link} className="block">
              <div className="relative aspect-[4/5] w-full overflow-hidden bg-muted/30">
                {product.featured_image_url ? (
                  <img
                    src={product.featured_image_url}
                    alt={product.title}
                    loading="lazy"
                    decoding="async"
                    className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.02]"
                  />
                ) : (
                  <div className="h-full w-full bg-muted/40" />
                )}
              </div>
              <div className="mt-6 flex items-baseline justify-between gap-4">
                <h3 className="font-default text-sm font-medium text-foreground">
                  <span className="relative inline-block">
                    {product.title}
                    <span className="absolute -bottom-1 left-0 h-px w-0 bg-border transition-all duration-300 ease-out group-hover:w-full" />
                  </span>
                </h3>
                <span className="font-default text-sm text-muted-foreground">
                  {product.discounted_price !== undefined && product.discounted_price !== null && product.discounted_price > 0
                    ? `₹${Number(product.discounted_price).toLocaleString()}`
                    : product.price !== undefined && product.price !== null
                    ? `₹${Number(product.price).toLocaleString()}`
                    : ""}
                </span>
              </div>
            </Link>
            <div className="mt-4">
              <AddToCartButton
                productId={product.id}
                variant="link"
                className="w-full"
                buttonClassName="bg-primary text-primary-foreground"
                text="Book Journey"
                showQuantity={false}
              />
            </div>
          </article>
        ))}
      </div>

      {totalPages > 1 && (
        <nav className="flex items-center justify-center gap-6" aria-label="Pagination">
          <Button
            variant="link"
            onClick={prevPage}
            disabled={!hasPrev}
            className="font-default text-sm font-medium text-foreground decoration-accent-foreground underline-offset-4 hover:underline disabled:opacity-30"
          >
            {"\u2190 Previous"}
          </Button>
          <ul className="flex items-center gap-4">
            {pageNumbers.map((p, i) => (
              <li key={p} data-index={i}>
                <Button
                  variant="link"
                  onClick={() => setPage(p)}
                  className={
                    p === currentPage
                      ? "font-default text-sm font-medium text-foreground underline decoration-accent-foreground decoration-1 underline-offset-4"
                      : "font-default text-sm font-medium text-muted-foreground hover:text-foreground"
                  }
                >
                  {p}
                </Button>
              </li>
            ))}
          </ul>
          <Button
            variant="link"
            onClick={nextPage}
            disabled={!hasNext}
            className="font-default text-sm font-medium text-foreground decoration-accent-foreground underline-offset-4 hover:underline disabled:opacity-30"
          >
            {"Next \u2192"}
          </Button>
        </nav>
      )}
    </div>
  );
}

export default function Shop() {
  const currentWPQueryParams =
    typeof window !== "undefined" && (window as any).wvcClient
      ? (window as any).wvcClient.getCurrentWPQueryParams()
      : {};
  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section data-section-id="13359"
      id="shop-content"
      className="bg-background text-foreground py-16 md:py-24"
    >
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-20">
        <EcommerceProductsProvider wp_query={wp_query}>
          <div className="mb-10 flex items-center justify-between border-b border-border pb-4 lg:hidden">
            <Sheet>
              <SheetTrigger asChild>
                <Button
                  variant="link"
                  className="px-0 font-default text-[13px] font-medium uppercase tracking-[0.14em] text-foreground"
                >
                  Filter & Sort
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-[85vw] max-w-sm overflow-y-auto bg-background p-6">
                <SheetHeader className="px-0">
                  <SheetTitle className="font-default text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    Refine Search
                  </SheetTitle>
                </SheetHeader>
                <div className="mt-6 space-y-8">
                  <div className="border-t border-border pt-8">
                    <ProductsFilter />
                  </div>
                </div>
              </SheetContent>
            </Sheet>
            <span className="font-default text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              Innoleaf Collection
            </span>
          </div>

          <div className="flex gap-16">
            <aside className="hidden w-1/4 shrink-0 lg:block">
              <div className="sticky top-[120px]">
                <p className="mb-8 font-default text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                  Refine Search
                </p>
                <div className="border-t border-border/60">
                  <ProductsFilter />
                </div>
              </div>
            </aside>

            <div className="w-full lg:w-3/4">
              <div className="mb-12 hidden items-end justify-between gap-6 border-b border-border pb-6 lg:flex">
                <div>
                  <p className="font-default text-[11px] font-medium uppercase tracking-[0.18em] text-muted-foreground">
                    The Selection
                  </p>
                  <h2 className="mt-3 font-serif text-3xl font-light tracking-tight text-foreground md:text-4xl">
                    Our <em className="italic">destinations</em>
                  </h2>
                </div>
                <div className="shrink-0">
                  <ProductSortDropdown label="Sort by" />
                </div>
              </div>

              <ShopGridConsumer />
            </div>
          </div>
        </EcommerceProductsProvider>
      </div>
    </section>
  );
}